import React, { useCallback, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import { createUseStyles } from 'react-jss';
import classnames from 'classnames';
import { Checkbox } from '@bingads-webui-react/primitive-with-signal';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { AddressForm } from '@bingads-webui-cc-react/address-form';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { UserCountrySelector } from '../controls';
import { Forms, addressFormProperties } from '../constants';
import { AddressPropTypes, PassiveErrorsShapes, FormControlsShape, helpPopupIdsShape } from '../shapes';
import { getBaseABLStyles } from '../styles';

const { twoLinesFormat } = AddressUtils;

const useStyles = createUseStyles(getBaseABLStyles());

export const UserLocationWrapper = ({
  i18n,
  formControls,
  userCountryCode,
  passiveErrors,
  alwaysShowError,
  userCountryList,
  provinceList,
  getStatesForCountryCode,
  disableCurrencyAutoSelect,
  onUserCountryChange,
  userAddress,
  onUserAddressChange,
  isReadonly,
  isUseSameLocationChecked,
  setIsUseSameLocationChecked,
  helpPopupIds,
}) => {
  const classes = useStyles();
  // LocationAndCurrency pilot: resolve states on demand for the requested
  // country to avoid the stale prefetched-provinceList race on country change.
  // When the pilot is off, keep resolving the prefetched list. (LocationAndCurrency)
  const findStates = useCallback(
    requestedCountryCode => (disableCurrencyAutoSelect && _.isFunction(getStatesForCountryCode)
      ? getStatesForCountryCode(requestedCountryCode)
      : Promise.resolve(provinceList)),
    [disableCurrencyAutoSelect, getStatesForCountryCode, provinceList]
  );
  const userCountryName = useMemo(() => _.get(_.findWhere(userCountryList, { Code: userCountryCode }), 'Name'), [userCountryCode, userCountryList]);

  const onUseSameLocationChange = () => {
    setIsUseSameLocationChecked(prev => !prev);
  };

  if (!formControls[Forms.UserLocation].visible) {
    return null;
  }

  if (isReadonly) {
    return (
      <LineByLineFormGroup
        contentColClass={classnames('col-xs-6', 'user-location-control')}
        helpPopupTopic={helpPopupIds[Forms.UserLocation].popupId}
        helpPopupAriaLabel={helpPopupIds[Forms.UserLocation].ariaLabel}
        label={i18n.getString(_TL_('User location'))}
        id="user-address-static-form"
        className="user-address-static-form"
        isStatic
      >
        <Checkbox id="user-location-use-same-checkbox" disabled checked={isUseSameLocationChecked}>
          {i18n.getString(_TL_('Apply above business location to the user'))}
        </Checkbox>
        {!isUseSameLocationChecked ? (twoLinesFormat(userAddress).join('\n') || i18n.getString(_TL_('(Not set)'))) : null}
      </LineByLineFormGroup>
    );
  }

  return (
    <div className={classnames('user-location-form-wrapper')}>
      <LineByLineFormGroup
        contentColClass={classnames('col-xs-6')}
        helpPopupTopic={helpPopupIds[Forms.UserLocation].popupId}
        helpPopupAriaLabel={helpPopupIds[Forms.UserLocation].ariaLabel}
        label={i18n.getString(_TL_('User location'))}
      >
        <Checkbox id="user-location-use-same-checkbox" onChange={onUseSameLocationChange} checked={isUseSameLocationChecked}>
          {i18n.getString(_TL_('Apply above business location to the user'))}
        </Checkbox>
      </LineByLineFormGroup>
      {!isUseSameLocationChecked && (
        <UserCountrySelector
          i18n={i18n}
          userCountryCode={userCountryCode}
          userCountryList={userCountryList}
          onUserCountryChange={onUserCountryChange}
          alwaysShowError={alwaysShowError}
          isOptional={formControls[Forms.UserLocation].optional}
          disabled={formControls[Forms.UserLocation].disabled}
        />)
      }
      {
        !isUseSameLocationChecked && formControls[Forms.UserAddressForm].visible && (
          <LineByLineFormGroup showLabel={false}>
            <AddressForm
              id="user-address-form"
              className={classes.addressForm}
              i18n={i18n}
              findStates={findStates}
              countries={[{ Code: userCountryCode, Name: userCountryName }]}
              model={_.pick(userAddress, addressFormProperties)}
              onAddressChange={onUserAddressChange}
              showFieldsLabel
              showSectionLabel
              alwaysShowError={alwaysShowError}
              hideSingleCountry
              useDropdownList
              showPlaceholder
              useModelAsState
              passiveErrors={passiveErrors.UserAddressForm}
              isOptional={formControls[Forms.UserAddressForm].optional}
            />
          </LineByLineFormGroup>)
      }
    </div>
  );
};

UserLocationWrapper.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  formControls: FormControlsShape.isRequired,
  userCountryCode: PropTypes.string,
  userCountryList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  provinceList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getStatesForCountryCode: PropTypes.func,
  disableCurrencyAutoSelect: PropTypes.bool,
  onUserCountryChange: PropTypes.func.isRequired,
  alwaysShowError: PropTypes.bool.isRequired,
  userAddress: AddressPropTypes,
  onUserAddressChange: PropTypes.func.isRequired,
  isReadonly: PropTypes.bool.isRequired,
  passiveErrors: PassiveErrorsShapes,
  helpPopupIds: helpPopupIdsShape,
};

UserLocationWrapper.defaultProps = {
  userCountryCode: null,
  getStatesForCountryCode: null,
  disableCurrencyAutoSelect: false,
  userAddress: {},
  passiveErrors: {},
  helpPopupIds: {},
};
